import { digestSchema } from "@wags/agent-contracts/common.contract.ts";
import {
    agentLineMigrationIdSchema,
    agentLineNameSchema,
} from "@wags/agent-contracts/line-migration.contract.ts";
import { z } from "zod";
import { authoredToolResultsSchema } from "@wags/agent-contracts/tool-result.contract.ts";

import {
    agentMessageSchema,
    agentUsageSchema,
    boundedJsonSchema,
    serializedAgentErrorSchema,
    timestampSchema,
} from "./common.contract.ts";

export const MAX_EVENT_DELTA_TEXT_LENGTH = 32_768;
export const MAX_PUBLIC_EVENT_BYTES = 750_000;

const toolEventFields = {
    toolCallId: z.string().min(1),
    toolId: z.string().min(1),
    summary: z.string().optional(),
};

const toolCompletedEventSchema = z.preprocess(
    (value) => {
        if (
            typeof value !== "object" ||
            value === null ||
            !("data" in value) ||
            "metadata" in value
        ) {
            return value;
        }
        const { data, ...event } = value as Record<string, unknown>;
        return { ...event, metadata: { publicData: data } };
    },
    z
        .object({
            type: z.literal("tool.completed"),
            ...toolEventFields,
            metadata: z
                .object({
                    publicData: boundedJsonSchema.optional(),
                    authoredToolResults: authoredToolResultsSchema.optional(),
                })
                .strict()
                .optional(),
        })
        .strict(),
);

export const agentPublicEventPayloadSchema = z.union([
    z.object({ type: z.literal("event.redacted") }).strict(),
    z
        .object({
            type: z.literal("input.accepted"),
            inputId: z.string().min(1),
            external: boundedJsonSchema.optional(),
        })
        .strict(),
    z
        .object({
            type: z.literal("session.automation.changed"),
            state: z.enum(["active", "escalated", "archived"]),
            preserveActiveTurnId: z.string().min(1).optional(),
            reason: z.string().optional(),
        })
        .strict(),
    z
        .object({
            type: z.literal("session.release.changed"),
            previousReleaseId: digestSchema,
            previousDeploymentRevisionId: digestSchema,
            releaseId: digestSchema,
            deploymentRevisionId: digestSchema,
        })
        .strict(),
    z
        .object({
            type: z.literal("session.line.changed"),
            migrationId: agentLineMigrationIdSchema,
            previousLine: agentLineNameSchema,
            line: agentLineNameSchema,
        })
        .strict(),
    z.object({ type: z.literal("turn.started") }).strict(),
    z
        .object({
            type: z.literal("message.delta"),
            messageId: z.string().min(1),
            text: z.string().min(1).max(MAX_EVENT_DELTA_TEXT_LENGTH),
            visibility: z.literal("web").optional(),
        })
        .strict(),
    z
        .object({
            type: z.literal("reasoning.delta"),
            messageId: z.string().min(1),
            text: z.string().min(1).max(MAX_EVENT_DELTA_TEXT_LENGTH),
        })
        .strict(),
    z
        .object({
            type: z.literal("message.completed"),
            message: agentMessageSchema,
        })
        .strict(),
    z
        .object({
            type: z.literal("tool.started"),
            ...toolEventFields,
            input: boundedJsonSchema.optional(),
        })
        .strict(),
    toolCompletedEventSchema,
    z
        .object({
            type: z.literal("tool.failed"),
            ...toolEventFields,
            error: serializedAgentErrorSchema,
        })
        .strict(),
    z
        .object({
            type: z.literal("turn.completed"),
            usage: agentUsageSchema.optional(),
        })
        .strict(),
    z
        .object({
            type: z.literal("turn.failed"),
            error: serializedAgentErrorSchema,
        })
        .strict(),
    z
        .object({
            type: z.literal("turn.cancelled"),
            reason: z.string().optional(),
        })
        .strict(),
    z
        .object({
            type: z.literal("turn.interrupted"),
            reason: z.string().min(1),
        })
        .strict(),
    z.object({ type: z.literal("session.deleting") }).strict(),
    z.object({ type: z.literal("session.deleted") }).strict(),
]);

export const agentPublicEventSchema = z
    .object({
        protocolVersion: z.literal(1),
        sessionId: z.string().min(1),
        cursor: z.number().int().positive(),
        eventId: z.string().min(1),
        createdAt: timestampSchema,
        agentId: z.string().min(1),
        line: agentLineNameSchema.optional(),
        releaseId: digestSchema,
        deploymentRevisionId: digestSchema,
        turnId: z.string().min(1).optional(),
        causationInputId: z.string().min(1).optional(),
        payload: agentPublicEventPayloadSchema,
    })
    .strict()
    .superRefine((event, context) => {
        if (
            event.payload.type === "message.completed" &&
            event.payload.message.completionCursor !== event.cursor
        ) {
            context.addIssue({
                code: "custom",
                path: ["payload", "message", "completionCursor"],
                message: "Message completion cursor must match event cursor",
            });
        }
    });

export type AgentPublicEvent = z.infer<typeof agentPublicEventSchema>;
export type AgentPublicEventPayload = z.infer<
    typeof agentPublicEventPayloadSchema
>;

export const agentTraceContextSchema = z
    .object({
        traceId: z.uuid().optional(),
        sessionId: z.string().min(1),
        turnId: z.string().min(1),
        conversationId: z.string().min(1).optional(),
        parentSessionId: z.string().min(1).optional(),
        parentTurnId: z.string().min(1).optional(),
        parentToolCallId: z.string().min(1).optional(),
        delegationId: z.string().min(1).optional(),
    })
    .catchall(z.string());
export type AgentTraceContext = z.infer<typeof agentTraceContextSchema>;

export const agentRuntimeCausationSchema = z
    .object({
        conversationId: z.string().min(1).optional(),
        parentSessionId: z.string().min(1).optional(),
        parentTurnId: z.string().min(1).optional(),
        parentToolCallId: z.string().min(1).optional(),
        delegationId: z.string().min(1).optional(),
    })
    .strict();

const runtimeBase = {
    schemaVersion: z.literal(1).optional(),
    sessionId: z.string().min(1),
    agentId: z.string().min(1),
    line: agentLineNameSchema.optional(),
    releaseId: digestSchema,
    deploymentRevisionId: digestSchema,
    causation: agentRuntimeCausationSchema,
};

export const agentRuntimeSessionCreatedDataSchema = z
    .object({
        ...runtimeBase,
        createdAt: timestampSchema,
        expiresAt: timestampSchema,
    })
    .strict();

export const agentRuntimeSessionLifecycleDataSchema = z
    .object({
        ...runtimeBase,
        occurredAt: timestampSchema,
        eventId: z.string().min(1).optional(),
        cursor: z.number().int().positive().optional(),
    })
    .strict();

export const agentRuntimeTurnLifecycleDataSchema = z
    .object({
        ...runtimeBase,
        occurredAt: timestampSchema,
        eventId: z.string().min(1),
        cursor: z.number().int().positive(),
        turnId: z.string().min(1),
        causationInputId: z.string().min(1),
        errorCode: z.string().min(1).optional(),
        errorMessage: z.string().max(16_384).optional(),
        retryable: z.boolean().optional(),
        usage: agentUsageSchema.optional(),
        reason: z.string().min(1).optional(),
    })
    .strict();

export const agentRuntimeToolLifecycleDataSchema =
    agentRuntimeTurnLifecycleDataSchema
        .omit({ errorCode: true, reason: true, usage: true })
        .extend({
            toolCallId: z.string().min(1),
            toolId: z.string().min(1),
            errorCode: z.string().min(1).optional(),
        })
        .strict();

export const agentRuntimeMessageCommittedDataSchema = z
    .object({
        ...runtimeBase,
        occurredAt: timestampSchema,
        eventId: z.string().min(1),
        cursor: z.number().int().positive(),
        turnId: z.string(),
        causationInputId: z.string().min(1).optional(),
        messageId: z.string().min(1),
        completionCursor: z.number().int().positive(),
        role: z.enum(["user", "assistant", "system", "tool"]),
        textPreview: z.string().max(8_192).optional(),
        contentHash: z.string().regex(/^sha256:[0-9a-f]{64}$/u),
        contentBytes: z.number().int().nonnegative(),
    })
    .strict();

export const agentRuntimeSinkFlushObservedDataSchema = z
    .object({
        sessionId: z.string().min(1).max(512),
        sinkId: z.string().min(1).max(2_048),
        agentId: z.string().min(1).max(256),
        line: agentLineNameSchema.optional(),
        releaseId: digestSchema,
        deploymentRevisionId: digestSchema,
        afterCursor: z.number().int().nonnegative(),
        throughCursor: z.number().int().nonnegative(),
        attemptCount: z.number().int().positive(),
        itemCount: z.number().int().nonnegative().max(500),
        startedAtMs: z.number().int().nonnegative(),
        preparedAtMs: z.number().int().nonnegative(),
        bindingStartedAtMs: z.number().int().nonnegative().nullable(),
        bindingFinishedAtMs: z.number().int().nonnegative().nullable(),
        finishedAtMs: z.number().int().nonnegative(),
        result: z.enum(["delivered", "failed"]),
        remaining: z.boolean().nullable(),
    })
    .strict();

const migrationTelemetryBase = {
    ...runtimeBase,
    migrationId: agentLineMigrationIdSchema,
    from: agentLineNameSchema,
    to: agentLineNameSchema,
    occurredAt: timestampSchema,
};

export const agentRuntimeMigrationStartedDataSchema = z
    .object(migrationTelemetryBase)
    .strict();
export const agentRuntimeMigrationCompletedDataSchema = z
    .object({
        ...migrationTelemetryBase,
        durationMs: z.number().nonnegative(),
    })
    .strict();
export const agentRuntimeMigrationFailedDataSchema = z
    .object({
        ...migrationTelemetryBase,
        durationMs: z.number().nonnegative(),
        errorName: z.string().max(256),
        errorMessage: z.string().max(1024),
    })
    .strict();

export const agentRuntimeMigrationTelemetryEventSchema = z.discriminatedUnion(
    "type",
    [
        z
            .object({
                type: z.literal("agent.runtime.migration.started"),
                data: agentRuntimeMigrationStartedDataSchema,
            })
            .strict(),
        z
            .object({
                type: z.literal("agent.runtime.migration.completed"),
                data: agentRuntimeMigrationCompletedDataSchema,
            })
            .strict(),
        z
            .object({
                type: z.literal("agent.runtime.migration.failed"),
                data: agentRuntimeMigrationFailedDataSchema,
            })
            .strict(),
    ],
);

export type AgentRuntimeMigrationTelemetryEvent = z.infer<
    typeof agentRuntimeMigrationTelemetryEventSchema
>;

export const agentRuntimeTelemetryEventSchema = z.discriminatedUnion("type", [
    z
        .object({
            type: z.literal("agent.runtime.session.created"),
            data: agentRuntimeSessionCreatedDataSchema,
        })
        .strict(),
    ...(["deleting", "deleted"] as const).map((status) =>
        z
            .object({
                type: z.literal(`agent.runtime.session.${status}`),
                data: agentRuntimeSessionLifecycleDataSchema,
            })
            .strict(),
    ),
    ...(
        ["started", "completed", "failed", "cancelled", "interrupted"] as const
    ).map((status) =>
        z
            .object({
                type: z.literal(`agent.runtime.turn.${status}`),
                data: agentRuntimeTurnLifecycleDataSchema,
            })
            .strict(),
    ),
    ...(["started", "completed", "failed"] as const).map((status) =>
        z
            .object({
                type: z.literal(`agent.runtime.tool.${status}`),
                data: agentRuntimeToolLifecycleDataSchema,
            })
            .strict(),
    ),
    z
        .object({
            type: z.literal("agent.runtime.message.committed"),
            data: agentRuntimeMessageCommittedDataSchema,
        })
        .strict(),
    z
        .object({
            type: z.literal("agent.runtime.sink.flush.observed"),
            data: agentRuntimeSinkFlushObservedDataSchema,
        })
        .strict(),
]);

export type AgentRuntimeTelemetryEvent = z.infer<
    typeof agentRuntimeTelemetryEventSchema
>;
